// Карточка записи чёрного списка. Вкладок нет: у записи один связный набор полей — кто, за
// что и до какого числа. Восемь вкладок ради шести строк были бы поверхностью, за которой
// ничего не стоит.
//
// Действия живут в шапке карточки, рядом со своим объектом (правило 3): снять запись и
// уничтожить персональные данные. Первое — панель, потому что снятие обратимо заведением
// новой записи и ничего не стирает; второе — модалка, потому что обратной кнопки у него нет.
//
// ## Снятая запись объясняет себя ПЕРВОЙ строкой
//
// Тз 5.8.2 и сценарий 90 требуют, чтобы при снятии сохранялись дата, автор и причина. Прятать
// их среди прочих полей нельзя: человек открывает карточку снятой записи ровно затем, чтобы
// узнать, почему её сняли, — и если ответ лежит седьмой строкой, он идёт спрашивать в чат.

import { RiArrowGoBackLine, RiErrorWarningLine, RiUserForbidLine } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import * as Alert from '@/shared/ui/alert';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { formatDateDMY, formatDateOnly, parseMoment } from '@/shared/time/format';

import type { BlacklistEntryOut, BlacklistStatus } from '../api/blacklist';
import { useBlacklistEntryQuery } from '../queries';
import type { HiringRoute } from '../route';
import { BLANK, PANEL_ENTRY_LIFT, PANEL_NEW_ENTRY, openPanelHref } from '../route';

import { entryTitle, hasPersonalData } from '../views/BlacklistList';
import { EraseDialog } from './EraseDialog';

const STATUS_COLORS: Readonly<Record<BlacklistStatus, 'red' | 'gray'>> = {
  active: 'red',
  lifted: 'gray',
};

export function BlacklistDetail({ route }: { route: HiringRoute }) {
  if (route.rowId === undefined) {
    return (
      <DetailPane>
        <div className='flex h-full items-center justify-center'>
          <EmptyState
            icon={RiUserForbidLine}
            title='Запись не выбрана'
            description='Выберите запись в списке слева — здесь откроется, кто в ней, за что он внесён, на каком правовом основании и до какого числа запись хранится.'
            // Кнопки «Внести запись» здесь НЕТ намеренно: такая же ссылка уже стоит в левой
            // колонке — внизу списка и в его пустом состоянии. Два приглашения к одному
            // действию разными словами читаются как два разных действия, и человек ищет
            // между ними разницу, которой нет.
          />
        </div>
      </DetailPane>
    );
  }
  return <Карточка entryId={route.rowId} route={route} />;
}

function Карточка({ entryId, route }: { entryId: string; route: HiringRoute }) {
  const navigate = useNavigate();
  const query = useBlacklistEntryQuery(entryId);

  return (
    <DetailPane
      title={
        <span className='flex min-w-0 items-center gap-2'>
          <RiUserForbidLine className='size-5 shrink-0 text-text-soft-400' />
          <span className='truncate'>
            {query.data === undefined ? 'Запись чёрного списка' : entryTitle(query.data)}
          </span>
          {query.data !== undefined && (
            <Badge.Root
              variant='lighter'
              color={STATUS_COLORS[query.data.status as BlacklistStatus] ?? 'gray'}
              size='medium'
            >
              {query.data.status_title}
            </Badge.Root>
          )}
        </span>
      }
      actions={
        query.data === undefined ? undefined : (
          <>
            {/* Снятие предлагается только у действующей записи: снимать снятую нечего, а
                кнопка, которая ничего не изменит, — это отказ на два нажатия позже. */}
            {query.data.status === 'active' && (
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_ENTRY_LIFT, entryId))}
              >
                <Button.Icon as={RiArrowGoBackLine} />
                Снять запись
              </Button.Root>
            )}
            <EraseDialog entry={query.data} />
          </>
        )
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='карточка записи чёрного списка'
        isEmpty={() => false}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3 p-5'>
            <Skeleton className='h-24 w-full' />
            <Skeleton className='h-24 w-full' />
          </div>
        }
        empty={null}
      >
        {(entry) => <Содержимое entry={entry} route={route} />}
      </ScreenState>
    </DetailPane>
  );
}

function Содержимое({ entry, route }: { entry: BlacklistEntryOut; route: HiringRoute }) {
  const снята = entry.status === 'lifted';
  const снятаКогда = parseMoment(entry.lifted_at);
  const внесенаКогда = parseMoment(entry.created_at);
  const естьДанные = hasPersonalData(entry);
  const паспорт = [entry.passport_series, entry.passport_number]
    .map((часть) => (часть ?? '').trim())
    .filter((часть) => часть !== '')
    .join(' ');

  return (
    <div className='flex flex-col gap-3 p-5'>
      {снята && (
        // Плашка, а не поле в общем списке: это ответ на главный вопрос к снятой записи, и он
        // обязан читаться до того, как человек начнёт вчитываться в остальное.
        <Alert.Root variant='light' status='information' size='small' role='status'>
          <Alert.Icon as={RiArrowGoBackLine} />
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span>
              Запись снята{датаСнятияТекстом(снятаКогда)}. Сверка её больше не находит: этот человек
              снова проходит проверку чистым.
            </span>
            <span className='text-paragraph-xs opacity-80'>
              {(entry.lifted_reason ?? '').trim() === ''
                ? 'Причина снятия не записана.'
                : `Причина снятия: ${entry.lifted_reason}`}
            </span>
            <span className='text-paragraph-xs opacity-80'>
              Кто снял — видно в журнале действий.
            </span>
          </div>
        </Alert.Root>
      )}

      {!естьДанные && (
        // Запись без единого признака человека. Она остаётся поводом и следом решения, но
        // сверка по ней не сработает никогда: сравнивать не с чем. Это и есть главное, что
        // надо сказать вслух, — иначе пустые поля читаются как «данные не догрузились».
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span>
              Персональных данных в записи нет: ни фамилии, ни даты рождения, ни телефона, ни
              паспорта, ни удостоверения.
            </span>
            <span className='text-paragraph-xs opacity-80'>
              Их либо уничтожили по требованию человека, либо запись завели без них. Остались
              причина, правовое основание и сам факт записи — но сверка по такой записи никого не
              найдёт: если этот человек придёт наниматься снова, его пропустят.
            </span>
            <div className='mt-1'>
              <LinkButton.Root variant='primary' size='small' underline asChild>
                <Link to={openPanelHref(route, PANEL_NEW_ENTRY, BLANK)}>
                  Внести запись с данными человека
                </Link>
              </LinkButton.Root>
            </div>
          </div>
        </Alert.Root>
      )}

      {естьДанные && (
        <FieldList title='Кто в записи'>
          <FieldRow
            label='Фамилия, имя, отчество'
            value={имяИлиНичего(entry)}
            missing='не записано: человека узнают по другим признакам'
          />
          <FieldRow
            label='Дата рождения'
            value={(entry.birth_date ?? '').trim() === '' ? null : formatDateOnly(entry.birth_date)}
            missing='не указана'
            note='Вместе с фамилией это второй по силе признак сверки после телефона.'
          />
          <FieldRow
            label='Телефон'
            value={пустоеВNull(entry.phone_normalized)}
            missing='не указан'
            note='Самый сильный признак: по нему совпадение считается точным.'
          />
          <FieldRow
            label='Паспорт'
            value={паспорт === '' ? null : паспорт}
            missing='не указан'
            note='Серия и номер вместе: по половине сверка не сработает.'
          />
          <FieldRow
            label='Удостоверение частного охранника'
            value={пустоеВNull(entry.guard_license_no)}
            missing='не указано'
          />
          <FieldRow
            label='Регион'
            value={пустоеВNull(entry.region)}
            missing='не указан'
            note='Список общий по всей группе юрлиц: регион сужает поиск похожих фамилий, но не прячет запись от других регионов.'
          />
        </FieldList>
      )}

      <FieldList title='Почему в списке'>
        <FieldRow
          label='Причина'
          value={entry.reason_title}
          note={пустоеВNull(entry.reason_note) ?? undefined}
        />
        <FieldRow
          label='Правовое основание'
          value={пустоеВNull(entry.legal_basis)}
          missing='не записано'
          note='Указывает заказчик: система оснований не придумывает.'
        />
        <FieldRow
          label='Срок хранения'
          value={formatDateOnly(entry.retention_until)}
          note='После этой даты запись уничтожается, и человек снова проходит сверку чистым.'
        />
        <FieldRow
          label='Внесена в список'
          value={внесенаКогда === null ? null : formatDateDMY(внесенаКогда)}
          missing='дата не записана'
        />
        <FieldRow
          label='Кто внёс'
          value={null}
          // Правило 7: неизвестное отличается от пустого и называет, кто ответит. Имя
          // автора в карточку не приезжает, а показывать вместо него опознаватель — значит
          // показывать человеку служебное обозначение вместо ответа.
          missing='видно в журнале действий'
        />
      </FieldList>
    </div>
  );
}

/** ФИО, если оно есть; иначе null — чтобы поле показало причину, а не пустую строку. */
function имяИлиНичего(entry: BlacklistEntryOut): string | null {
  const фио = [entry.last_name, entry.first_name, entry.middle_name]
    .map((часть) => (часть ?? '').trim())
    .filter((часть) => часть !== '')
    .join(' ');
  return фио === '' ? null : фио;
}

/** Пустая строка с сервера — это отсутствие значения, а не значение из пробелов. */
function пустоеВNull(значение: string | null | undefined): string | null {
  const текст = (значение ?? '').trim();
  return текст === '' ? null : текст;
}

/** « 20.08.2026» либо пусто: фраза обязана читаться и без даты, если её не сохранили. */
function датаСнятияТекстом(когда: Date | null): string {
  return когда === null ? '' : ` ${formatDateDMY(когда)}`;
}
